import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import * as Atom from '../recoil';
import { fetchBaseCustomer } from '../../infra/api/fetchBaseCustomer';
import { useBaseParam } from '../hooks/useBaseParam';
import { fetchCommonCodes } from '../../infra/api/fetchCommonCodes';
import { fetchBaseInfo } from '../../infra/api/fetchBaseInfo';
import useBeacon from '../hooks/useBeacon';
import { beaconSelector } from '../recoil/selector';
import { fetchUrlFromSrcCd } from '../../infra/api/fetchUrlFromSrcCd';
import { useBridge } from '../hooks/useBridge';
import { MEDIA_CODE, MEDIA_NAME } from '../types/types';
import { isIOS } from 'react-device-detect';
import { fetchChallengeEntry } from '../../infra/api/fetchChallengeEntry';

const useEntry = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [shouldInitialize, setShouldInitialize] = useState<boolean>(false);
  const [isOpenBridgeModal, setIsBridgeModal] = useState<boolean>(true);
  const [isReady, setIsReady] = useState<boolean>(false);

  const { media } = useParams();
  const [searchParams] = useSearchParams();
  const [userInfo, setUserInfo] = useRecoilState(Atom.userInfo);
  const [, setPersonalInfo] = useRecoilState(Atom.personalInfo);
  const [, setCommonCodes] = useRecoilState(Atom.commonCodes);
  const [, setBaseContents] = useRecoilState(Atom.baseContents);
  const [, setRedirectInfo] = useRecoilState(Atom.redirectInfo);
  const [lguIOSWarning, setLguIOSWarning] = useRecoilState(Atom.lguIOSWarning);

  const getBaseCustomer = useBaseParam(fetchBaseCustomer, {
    ctn: userInfo.ctn,
    mgntCd: userInfo.mgntCd,
    srcCd: userInfo.srcCd,
    osType: userInfo.osType,
    mediaCd: userInfo.mediaCd,
    queueSeq: userInfo.queueSeq,
    passAppId: userInfo.passAppId,
    appUserId: userInfo.appUserId,
    appUserPartIdx: userInfo.appUserPartIdx,
    challengeSeq: userInfo.challengeSeq,
  });
  const getCommonCodes = useBaseParam(fetchCommonCodes, {
    codeGrpCdList: [
      'C014',
      'C001',
      'C002',
      'C005',
      'C003',
      'C004',
      'C008',
      'C009',
      'CRM01',
      'CRM02',
      'CRM03',
      'C046',
      'C047',
      'C048',
      'C049',
      'C050',
      'C052',
      'C056',
    ],
  });
  const getBaseInfo = useBaseParam(fetchBaseInfo, {});

  const br: any = useBridge(media);

  const navigateDA = (srcCd: string) => {
    setIsBridgeModal(false);
    if (media === 'skt') {
      br?.requestAddService({
        type: 'EBMP',
        url:
          process.env.REACT_APP_EBMP_SKT_URI +
          `?extraParam=3&extraParam2=${srcCd}`,
      });
    } else if (media === 'kt') {
      br?.requestAddService({
        type: 'EBMP',
        url: process.env.REACT_APP_EBMP_KT_URI + `?src_cd=${srcCd}`,
      });
    } else {
      const redirectYn = 'Y';
      br?.requestAddService({
        type: 'EBMP',
        url: `?tabSeq=1&track_cd_type=${srcCd}&redirectYn=${redirectYn}`,
      });
    }
  };

  // userInfo 세팅
  useEffect(() => {
    const params = Object.fromEntries(Array.from(searchParams));
    const userInfo = br.getParamInfo(params);

    setUserInfo({
      ...userInfo,
      media,
      osType: userInfo.osType || isIOS ? 'IOS' : 'AND',
      mediaCd: (() => {
        if (media === MEDIA_NAME.SKT) {
          return MEDIA_CODE.SKT;
        } else if (media === MEDIA_NAME.KT) {
          return MEDIA_CODE.KT;
        } else if (media === MEDIA_NAME.LGU) {
          return MEDIA_CODE.LGU;
        }
      })(),
    });
  }, []);

  const challengeEntry = useBaseParam(fetchChallengeEntry, {
    challengeSeq: userInfo.challengeSeq,
    appUserId: userInfo.appUserId,
    appUserPartIdx: userInfo.appUserPartIdx,
    mgntCd: userInfo.mgntCd,
    passAppId: userInfo.passAppId,
    mediaCd: userInfo.mediaCd
  });

  // baseInfo, commCode 세팅
  useEffect(() => {
    if (!Object.values(userInfo).filter((item) => item !== '').length) return;

    (async () => {
      const [_, resCommonCodes, resBaseInfo] = await Promise.all([
        userInfo.challengeSeq ? challengeEntry().catch(() => {}) : Promise.resolve(), // 새해 맞이 금고열기 이벤트 종료 후 주석
        getCommonCodes(),
        getBaseInfo(),
      ]);

      setBaseCustomer(resCommonCodes?.data?.result);
      setCommonCodes(resCommonCodes?.data?.result);
      setBaseContents(() => {
        return {
          ...resBaseInfo?.data?.result,
          serverTime: resBaseInfo?.data?.responseTime,
        };
      });
    })();
  }, [userInfo]);

  // baseCustomer 세팅
  const setBaseCustomer = async (commonCodes: any) => {
    const resBaseCustomer = await getBaseCustomer();
    if (resBaseCustomer?.data?.result) {
      const { transactionNo } = resBaseCustomer?.data;
      const {
        custNm,
        birthDt,
        gender,
        trackCd,
        ctn,
        custSeq,
        preRequestInfo,
        authToken,
      } = resBaseCustomer?.data?.result;

      // 가심사 후 30일 이내 유저 > personalInfo 세팅
      setPersonalInfo((prev: any) => {
        const commonInfo = {
          birthDt: birthDt,
          custNm: custNm,
          custSeq: custSeq,
          ctn: ctn,
          gender: gender,
          trackCd: trackCd,
          authToken: authToken ?? '',
          transactionNo: transactionNo,
        };
        if (preRequestInfo?.reuseYn === 'N') {
          return {
            ...prev,
            ...commonInfo,
          };
        } else {
          // 화면에 codeNm 노출을 위해서 매핑가능하도록 변경
          const jobs = commonCodes[1]?.list.map((item: any) => {
            return { text: item.codeNm, value: item.code };
          });
          const employee = commonCodes[2]?.list.map((item: any) => {
            return { text: item.codeNm, value: item.code };
          });
          const businessTyp = commonCodes[4]?.list.map((item: any) => {
            return { text: item.codeNm, value: item.code };
          });

          return {
            ...prev,
            ...commonInfo,
            objectTyp: preRequestInfo?.objectTyp,
            hopefulAmt: preRequestInfo?.hopefulAmt?.toString() ?? '',
            salaryAmt: preRequestInfo?.salaryAmt?.toString() ?? '',
            jobTyp: preRequestInfo?.jobTyp
              ? jobs?.find((item: any) => item.value === preRequestInfo?.jobTyp)
              : {},
            joinDt: preRequestInfo?.joinDt,
            employeeTyp: preRequestInfo?.employeeTyp
              ? employee?.find(
                  (item: any) => item.value === preRequestInfo?.employeeTyp,
                )
              : {},
            businessTyp: preRequestInfo?.businessTyp
              ? businessTyp?.find(
                  (item: any) => item.value === preRequestInfo?.businessTyp,
                )
              : {},
            houseTyp: preRequestInfo?.houseTyp,
            compRegNo: preRequestInfo?.compRegNo,
            compNm: preRequestInfo?.compNm,
            healthPayerTyp: preRequestInfo?.healthPayerTyp ?? '01',
            carOwnerYn: preRequestInfo?.carOwnerYn,
            carNo: preRequestInfo?.carNo,
            debtYn: preRequestInfo?.debtYn,
            debtTyp: preRequestInfo?.debtTyp,
            monthlyYn: preRequestInfo?.monthlyYn,
            regDate: preRequestInfo?.regDate,
            reuseYn: preRequestInfo?.reuseYn,
            scrapingYn: preRequestInfo?.scrapingYn ?? 'N',
          };
        }
      });
    } else {
      setPersonalInfo((prev: any) => {
        return {
          ...prev,
          custSeq: null,
        };
      });
    }
    setIsLoading(true);
  };

  //  beacon code
  const beaconData = useRecoilValue(beaconSelector);
  const beacon = useBeacon({
    mediaCd: beaconData.mediaCd,
    custSeq: beaconData.custSeq,
    srcCd: beaconData.srcCd,
  });
  useEffect(() => {
    if (isLoading) {
      beacon.sendSessionStart();
    }
  }, [isLoading]);
  useEffect(() => {
    if (isLoading && isReady) {
      setShouldInitialize(true);
      setIsBridgeModal(false);
    }
  }, [isLoading, isReady]);

  // todo remove
  window.onerror = (errorMsg, url, lineNumber, column, errorObj) => {
    beacon.sendIssueCatch({
      events: { subEventName: 'error' },
      properties: {
        type:
          'Error: ' +
          errorMsg +
          ' Script: ' +
          url +
          ' Line: ' +
          lineNumber +
          ' Column: ' +
          column +
          ' StackTrace: ' +
          JSON.stringify(errorObj),
      },
    });
  };

  // redirection code
  const getUrlFromSrcCd = useBaseParam(fetchUrlFromSrcCd, {
    srcCd: userInfo.srcCd,
  });

  const redirectionProcess = async () => {
    const response: any = await getUrlFromSrcCd();
    const data = response?.data?.result;
    if (
      data?.targetService === 'ebmp' &&
      data?.targetSrcCd &&
      data?.status === '10'
    ) {
      navigateDA(data?.targetSrcCd);
      return;
    } else if (data?.redirectionUrl && data?.status === '10') {
      const url = new URL(data?.redirectionUrl).pathname ?? '';
      const path = url
        .split('/')
        .filter((item) => !['skt', 'kt', 'lgu'].includes(item))
        .join('/');

      setRedirectInfo({ path });
      return;
    }
  };

  useEffect(() => {
    if (userInfo.srcCd) {
      (async () => {
        await redirectionProcess();
      })();
    }
  }, [userInfo.srcCd]);

  const onConfirmLguIOSWarning = () => {
    setLguIOSWarning({
      ...lguIOSWarning,
      showLguIosWarning: false,
    });
  };

  return {
    isLoading: shouldInitialize,
    media,
    isOpenBridgeModal,
    setIsReady,
    lguIOSWarning,
    onConfirmLguIOSWarning,
    hideHeader: userInfo.hideHeader,
  };
};

export default useEntry;
